// START IMPORTS
// Context imports
import {
  AppConfig,
  AppConstants,
  AppRequests,
  createAppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Theme and UI imports
import { styled } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  getSearchTypeForMenuItem,
  type ResponseError,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import { AlertProvider, type SideMenuItem } from '@ltvco/refresh-lib/v1';
// Vendor imports
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link,
} from 'react-router-dom';

// App-specific imports
import {
  constants,
  sideMenuItemsForNewDesign,
  searchTabsForNewDesign,
} from 'appConstants';
import { appRequests } from 'appRequests';
// END IMPORTS

// START STYLED_COMPONENTS
const MenuItem = styled(Link)<{ ga4EventType?: string }>(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.fontSize * 1.125,
  display: 'flex',
  alignItems: 'center',
  svg: {
    marginRight: theme.spacing(1),
  },
  width: '100%',
}));
// END STYLED_COMPONENTS

export function LibContextWrapper({
  children,
  trackEventGA4,
  trackEvent,
  logError,
  handleLogoutClick,
  isInSingleReportVariation,
}: {
  children: React.ReactNode | React.ReactNode[];
  trackEventGA4: (eventToTrack: object, eventName?: string) => void;
  trackEvent: (category: string, action: string, label?: string) => void;
  logError: (
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) => void;
  handleLogoutClick: () => void;
  isInSingleReportVariation: boolean;
}) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  const { isBlocked } = useSubscriptionAccessConfigContext();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const defaultConfig = createAppConfig();

  function flattenMenuItems(menuItems: SideMenuItem[]): SideMenuItem[] {
    return menuItems.flatMap(({ childItems, ...item }) => [
      item,
      ...(childItems?.length ? flattenMenuItems(childItems) : []),
    ]);
  }

  const getSubscriptionSearchTypeForMenuItem = (
    menuItem: SideMenuItem
  ): string =>
    getSearchTypeForMenuItem(menuItem.name, menuItem.route, menuItem.text) ??
    '';

  const flatMenu = flattenMenuItems(
    constants.config.sideMenuItems.filter((item) => {
      if (item.name === 'importList') {
        return false;
      }
      if (
        isInSingleReportVariation &&
        isBlocked(getSubscriptionSearchTypeForMenuItem(item))
      ) {
        return false;
      }

      return true;
    })
  );

  const menuItems = flatMenu.map((item) => {
    return (
      <MenuItem
        to={item.route}
        data-cy={`menu_${item['data-cy']}`}
        ga4EventType={item.ga4EventType}
      >
        {item.icon} {item.text}
      </MenuItem>
    );
  });

  function trackListhub(eventName: string, listingKey: string) {
    const listhubFn = (window as any).lh;
    if (listhubFn && typeof listhubFn !== 'undefined') {
      if (eventName && listingKey) {
        listhubFn('submit', eventName || null, { lkey: listingKey || null });
      }
    }
  }

  const appConfig = {
    ...defaultConfig,
    routingUtils: {
      ...defaultConfig.routingUtils,
      navigate,
      location,
      useSearchParams,
    },
    logError,
    trackEvent,
    trackEventGA4,
    trackListhub,
  };

  const appConstants = {
    ...constants,
    config: {
      ...constants.config,
      menuItems: [
        ...menuItems,
        <MenuItem
          to="#"
          onClick={() => handleLogoutClick()}
          data-cy="menu_log_out_btn"
        >
          Log Out
        </MenuItem>,
      ],
      sideMenuItems: [
        ...sideMenuItemsForNewDesign,
        {
          route: '',
          icon: '',
          text: 'Logout',
          name: 'logout',
          state: '',
          'data-cy': 'menu_log_out_btn',
          action: handleLogoutClick,
        },
      ],
      searchTabs: searchTabsForNewDesign,
    },
  };
  // END DERIVED_DATA_AND_UTILS

  // START RENDER
  return (
    <AlertProvider maxSnack={3} preventDuplicate autoHideDuration={5000}>
      <AppRequests.Provider value={appRequests}>
        <AppConfig.Provider value={appConfig as any}>
          <AppConstants.Provider value={appConstants}>
            {children}
          </AppConstants.Provider>
        </AppConfig.Provider>
      </AppRequests.Provider>
    </AlertProvider>
  );
  // END RENDER
}
